import { Component, Vue } from "vue-property-decorator";
type extraType = null | number | object;

// Наименование одной графы/строки формы расчёта из справочника
// dict_calculation_form_captions (см. calculation_form_captions_fast.py, EAK-1626).
// Ответ эндпоинта уже отфильтрован сервером под конкретные sheet_code='main'/output_code=
// 'screen' (см. loadCalcFormCaptions() в forms-handler-mixin-1.js), поэтому здесь - плоская
// карта по caption_key, без вложенности.
interface CalcFormCaptionValue {
    name_ru: string | null;
    name_kk: string | null;
    name_en: string | null;
}
type CalcFormCaptions = Record<string, CalcFormCaptionValue>;

@Component
export default class BpI18nHandlerMixin extends Vue {
    private getText(link: string, field: string, extra: extraType = null): string {
        const mainTextFieldName = link + field;
        if (extra) {
            if (typeof extra === 'number') return this.$tc(mainTextFieldName, extra)
            else return this.$t(mainTextFieldName, extra) as string;
        }
        return this.$t(mainTextFieldName) as string;
    }

    // Подставляет из справочника подписей (dict_calculation_form_captions, EAK-1626)
    // действующее наименование графы/строки таблицы для экрана (sheet_code='main',
    // output_code='screen' — расшифровки/report вне пилота), если оно там заведено;
    // иначе вызывает fallback (обычно this.getFormText(field, extra)/getCommonText(field)).
    //
    // ВАЖНО: это НЕ часть getText()/общих getXxxText()-геттеров — метод намеренно узкий
    // и предназначен для вызова ТОЛЬКО из computed tableFields() форм, явно подключивших
    // капшены (captionsEnabled: true) — сейчас Form01_123.vue, Form133.vue, Form02_149_v2.vue.
    // Если завести перехват прямо в getText(), он бы
    // применялся вообще ко всем вызовам getCommonText/getErrText/getUnitsText/... по всему
    // приложению (тосты, валидационные сообщения и т.п.), а справочник по спеке — только
    // про наименования граф/строк таблицы, не про произвольный UI-текст.
    //
    // Справочник (см. forms-handler-mixin-1.js, loadCalcFormCaptions()) хранится в reactive
    // data() подмешивающего компонента формы под именем `calcFormCaptions`, а не объявлен
    // здесь как computed/data - иначе имя схлопнется с ним же в data() потребителя миксина.
    // Читаем его через `this` напрямую (та же реактивная цепочка на $i18n.locale, что и
    // раньше у getText/$t()).
    public getCaptionText(field: string, extra: extraType, fallback: () => string): string {
        const captionText = this.resolveCaptionText(field);
        if (captionText !== null) {
            return this.interpolateCaptionText(captionText, extra);
        }
        return fallback();
    }

    private resolveCaptionText(field: string): string | null {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const captions = (this as any).calcFormCaptions as CalcFormCaptions | null | undefined;
        if (!captions) return null;
        const entry = captions[field];
        if (!entry) return null;
        const locale = this.$i18n.locale;
        let text: string | null = null;
        if (locale === 'kk') {
            text = entry.name_kk || entry.name_ru;
        } else if (locale === 'en') {
            text = entry.name_en || entry.name_ru;
        } else {
            text = entry.name_ru;
        }
        return text || null;
    }

    // Ручная интерполяция подстановок вида {year}/{coef} в тексте из справочника
    // (аналог встроенной интерполяции $t(), которая на текст из БД не применяется).
    private interpolateCaptionText(text: string, extra: extraType): string {
        if (!extra || typeof extra !== 'object') return text;
        return text.replace(/\{(\w+)\}/g, (_, key: string) => {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const value = (extra as any)[key];
            return (value === undefined || value === null) ? '' : String(value);
        });
    }

    private commonLink = 'modules.budget_request.calculation_forms.table_headers.';
    private reportsLink = 'modules.budget.budget_request';
    private budgetRequest = 'modules.budget_request';

    public getFilterText(field: string, extra: extraType = null): string {
        const link = `${this.budgetRequest}.filter.`;
        return this.getText(link, field, extra);
    }

    public getDuplDataText(field: string, extra: extraType = null): string {
        const link = `${this.budgetRequest}.duplicate_data.`;
        return this.getText(link, field, extra);
    }

    public getRepText(field: string, extra: extraType = null): string {
        const link = `${this.reportsLink}.`;
        return this.getText(link, field, extra);
    }

    public getUnitsText(field: string, extra: extraType = null): string {
        const link = `${this.commonLink}units.`;
        return this.getText(link, field, extra);
    }

    public getCommonText(field: string, extra: extraType = null): string {
        const link = `${this.commonLink}common.`;
        return this.getText(link, field, extra);
    }

    public getErrText(field: string, extra: extraType = null): string {
        const link = `${this.commonLink}errors.`;
        return this.getText(link, field, extra);
    }

    public getDecText(field: string, extra: extraType = null): string {
        const link = `${this.commonLink}decodes.`;
        return this.getText(link, field, extra);
    }

    public setFormText(form: string) {
        return (field: string, extra: extraType = null) => {
            const link = `${this.commonLink}${form}`;
            return this.getText(link, field, extra);
        }
    }

    public getDataTransferText(field: string, extra: extraType = null): string {
        const link = `${this.budgetRequest}.data_transfer.`;
        return this.getText(link, field, extra);
    }
}